/**
 * Phone number normalization utilities
 * Industry standard: store clean phone numbers, display user-friendly formats
 */

/**
 * Normalize phone number to E.164 format for storage
 * Handles country-specific formatting and ensures proper E.164 format
 * Examples:
 * "+1 (929) 462-2611" -> "+19294622611"
 * "+91 98765 43210" -> "+919876543210"
 * "+977-9876543210" -> "+9779876543210"
 */
export function normalizePhoneForStorage(phone: string, countryCode?: string): string {
  if (!phone) return '';
  
  // Remove all formatting characters except + and digits
  const normalized = phone.replace(/[^\d+]/g, '');
  
  // If phone already starts with + and looks like E.164, validate and return
  if (normalized.startsWith('+') && normalized.length >= 8 && normalized.length <= 16) {
    return normalized;
  }
  
  // If phone already includes country code at start, ensure + prefix
  if (!normalized.startsWith('+') && normalized.length > 10) {
    // Check if it starts with common country codes
    if (normalized.startsWith('1') && normalized.length === 11) return `+${normalized}`;
    if (normalized.startsWith('91') && normalized.length >= 12) return `+${normalized}`;
    if (normalized.startsWith('977') && normalized.length >= 13) return `+${normalized}`;
    if (normalized.startsWith('44') && normalized.length >= 12) return `+${normalized}`;
  }
  
  // Remove + if present to work with digits only
  const digitsOnly = normalized.replace(/^\+/, '');
  
  // If we have a country hint from the component, use it
  if (countryCode) {
    const countryDialCodes: Record<string, string> = {
      'US': '+1', 'CA': '+1', 'IN': '+91', 'NP': '+977', 'GB': '+44',
      'AU': '+61', 'DE': '+49', 'FR': '+33', 'JP': '+81', 'KR': '+82',
      'CN': '+86', 'SG': '+65', 'AE': '+971', 'BD': '+880', 'PK': '+92'
    };
    
    const dialCode = countryDialCodes[countryCode];
    if (dialCode) {
      return `${dialCode}${digitsOnly}`;
    }
  }
  
  // Smart auto-detection based on number patterns
  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`; // US/Canada default for 10 digits
  }
  
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return `+${digitsOnly}`; // US/Canada with country code
  }
  
  if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) {
    return `+${digitsOnly}`; // India
  }
  
  if (digitsOnly.length === 13 && digitsOnly.startsWith('977')) {
    return `+${digitsOnly}`; // Nepal
  }
  
  // Fallback: add + prefix and assume it's complete
  return `+${digitsOnly}`;
}

/**
 * Format phone number for user-friendly display (Google/Apple style)
 * Converts E.164 to readable format
 */
export function formatPhoneForDisplay(phone: string): string {
  if (!phone) return '';
  
  // Simple country-specific formatting
  if (phone.startsWith('+977') && phone.length === 14) {
    // Nepal: +9779803939607 -> 980-3939607
    return phone.substring(4, 7) + '-' + phone.substring(7);
  }
  
  if (phone.startsWith('+91') && phone.length === 13) {
    // India: +919876543210 -> 98765 43210
    return phone.substring(3, 8) + ' ' + phone.substring(8);
  }
  
  if (phone.startsWith('+1') && phone.length === 12) {
    // US: +15551234567 -> (555) 123-4567
    const digits = phone.substring(2);
    return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
  }
  
  if (phone.startsWith('+44')) {
    // UK: +447712345678 -> 07712 345678
    const national = phone.substring(3);
    if (national.length === 10) {
      return national.substring(0, 5) + ' ' + national.substring(5);
    }
  }
  
  // Default: return as-is for other formats
  return phone;
}

/**
 * Validate if a phone number is in a reasonable format
 * More lenient than strict E.164, accepts common input formats
 */
export function isValidPhoneFormat(phone: string): boolean {
  if (!phone) return false;
  
  // Remove all non-digit characters to check length
  const digitsOnly = phone.replace(/\D/g, '');
  
  // Must have at least 7 digits (local number) and max 15 (international standard)
  if (digitsOnly.length < 7 || digitsOnly.length > 15) {
    return false;
  }
  
  // Check format - allow digits, spaces, dashes, parentheses, plus
  const validFormat = /^[\+]?[\d\s\-\(\)]+$/.test(phone);
  
  return validFormat;
}

/**
 * Validate if a phone number is in proper E.164 format
 * E.164 format: + followed by country code and national number (max 15 digits total)
 */
export function isValidE164Format(phone: string): boolean {
  if (!phone) return false;
  
  // Must start with + and contain only digits after
  if (!/^\+\d+$/.test(phone)) return false;
  
  // Must be between 8 and 16 characters (+ plus 7-15 digits)
  if (phone.length < 8 || phone.length > 16) return false;
  
  // Country code validation (basic check)
  const digitsOnly = phone.substring(1); // Remove +
  
  // Common country code patterns
  if (digitsOnly.startsWith('1') && digitsOnly.length === 11) return true; // US/Canada
  if (digitsOnly.startsWith('44') && digitsOnly.length >= 10 && digitsOnly.length <= 13) return true; // UK
  if (digitsOnly.startsWith('91') && digitsOnly.length >= 10 && digitsOnly.length <= 13) return true; // India
  if (digitsOnly.startsWith('977') && digitsOnly.length >= 10 && digitsOnly.length <= 13) return true; // Nepal
  
  // General validation for other country codes (1-4 digit country code + national number)
  return digitsOnly.length >= 7 && digitsOnly.length <= 15;
}

/**
 * Extract country code from phone number (if present)
 */
export function extractCountryCode(phone: string): string | null {
  const normalized = normalizePhoneForStorage(phone);
  
  if (!normalized.startsWith('+')) return null;
  
  // Extract country codes based on known patterns
  const digitsOnly = normalized.substring(1);
  
  // Single digit country codes
  if (digitsOnly.startsWith('1')) return '+1'; // US/Canada
  
  // Two digit country codes  
  if (digitsOnly.startsWith('44')) return '+44'; // UK
  if (digitsOnly.startsWith('91')) return '+91'; // India
  
  // Three digit country codes
  if (digitsOnly.startsWith('977')) return '+977'; // Nepal
  
  // General extraction for other country codes (1-4 digits max)
  const countryCodeMatch = normalized.match(/^\+(\d{1,4})/);
  return countryCodeMatch ? `+${countryCodeMatch[1]}` : null;
}

/**
 * Get country information from phone number
 */
export function getCountryFromPhone(phone: string): { code: string; name: string; flag: string } | null {
  const countryCode = extractCountryCode(phone);
  
  if (!countryCode) return null;
  
  // Map of country codes to country info
  const countryMap: Record<string, { name: string; flag: string }> = {
    '+1': { name: 'United States', flag: '🇺🇸' },
    '+44': { name: 'United Kingdom', flag: '🇬🇧' },
    '+91': { name: 'India', flag: '🇮🇳' },
    '+977': { name: 'Nepal', flag: '🇳🇵' },
    '+86': { name: 'China', flag: '🇨🇳' },
    '+49': { name: 'Germany', flag: '🇩🇪' },
    '+33': { name: 'France', flag: '🇫🇷' },
    '+81': { name: 'Japan', flag: '🇯🇵' },
    '+82': { name: 'South Korea', flag: '🇰🇷' },
    '+61': { name: 'Australia', flag: '🇦🇺' },
  };
  
  const countryInfo = countryMap[countryCode];
  if (countryInfo) {
    return {
      code: countryCode,
      name: countryInfo.name,
      flag: countryInfo.flag
    };
  }
  
  return { code: countryCode, name: 'Unknown', flag: '🌍' };
}